import { useCallback, useEffect, useState } from "react";
import useRequest from "./useRequest";
import { API_ACTIVATION, API_CONNECTED_OBJECT, BASE_ADMIN, BASE_API } from "../constants/api";
import { RequestError } from "../errors/RequestError";
import { OwnConnectedObjectType } from "../types/connected-object";


type ActivateObjResponse = {
  message: string,
  data: Omit<OwnConnectedObjectType, 'feature'>
}

type Info = {
  message: string;
  data: {
    id: number;
    type: string;
  }
}

type InfoResponse = {
  status: number;
  message: string;
  data: {
    id: number;
    type: string | null;
  };
}

const isInfo = (error: Omit<RequestError, "name">): error is InfoResponse => {
  if (error.status && error.data && typeof error.data === "object") {
    return "id" in error.data && "type" in error.data;
  }
  return false;
}

export default function useActivationObj() {
  const [generalError, setGeneralError] = useState<Error|null>(null);
  
  const {
    error: activationError,
    response: activationResponse,
    request
  } = useRequest<ActivateObjResponse>();
  const [error, setError] = useState<string|null>(null);
  const [info, setInfo] = useState<Info|null>(null);

  useEffect(() => {
    if (activationError) {
      if (activationError instanceof RequestError) {
        if (activationError.status === 409 && isInfo(activationError)) {
          const {message, data} = activationError;
          const {id, type} = data;
          setInfo({message, data: {id, type: type || 'human'}});
        } else {
          setError(activationError.message);
        }
      } else {
        setGeneralError(activationError)
      }
    } else {
      setError(null);
      setInfo(null);
    }
  }, [activationError])

  const activateObj = useCallback((url: string) => {
    request(BASE_API, `${API_CONNECTED_OBJECT}/${API_ACTIVATION}`, {method: 'PUT', secure: true}, {url});
  }, [request])

  return {activateObj, generalError, error, info, success: activationResponse?.message, data: activationResponse?.data}
}

export function useAssignFor(userId: number) {
  const {error, response, request } = useRequest<ActivateObjResponse>();
  const [success, setSuccess] = useState<string|null>(null);

  useEffect(() => setSuccess(response?.message || null), [response])

  const assignObj = useCallback((url: string) => {
    setSuccess(null);
    request(BASE_ADMIN, `${API_CONNECTED_OBJECT}/${API_ACTIVATION}`, {method: 'PUT', secure: true}, {url, userId});
  }, [request, userId])


  return { success, error, assignObj}
}